import { useEffect } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { Toaster } from "@/components/ui/sonner";
import { Layout } from "@/components/Layout";
import Home from "@/pages/Home";
import About from "@/pages/About";
import Aunties from "@/pages/Aunties";
import Approach from "@/pages/Approach";
import Programs from "@/pages/Programs";
import Threads from "@/pages/Threads";
import WhoWeSupport from "@/pages/WhoWeSupport";
import Culture from "@/pages/Culture";
import Community from "@/pages/Community";
import Consultation from "@/pages/Consultation";
import Referrals from "@/pages/Referrals";
import Impact from "@/pages/Impact";
import Partner from "@/pages/Partner";
import Contact from "@/pages/Contact";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import RequestSupport from "@/pages/RequestSupport";
import PrivateSupports from "@/pages/PrivateSupports";
import NdisSupports from "@/pages/NdisSupports";
import Privacy from "@/pages/Privacy";
import Pricing from "@/pages/Pricing";
import CulturalGovernance from "@/pages/CulturalGovernance";
import { SHOW_GOVERNANCE } from "@/utils/flags";

const SEO_MAP = {
  "/": { t: "Ngawilyi Narrative | Aunties Narrative Therapy & Mentoring", d: "Ngawilyi means Aunty in Arabana. Find Ngawilyi Narrative—Aunties Narrative—for Aboriginal-owned narrative therapy, mentoring and community connection in South Australia." },
  "/about": { t: "Our Story | Ngawilyi Narrative", d: "Why Ngawilyi Narrative exists — an Aboriginal-owned organisation built around the idea that every family needs an extra Aunty for a yarn." },
  "/aunties": { t: "Meet Our Aunties | Ngawilyi Narrative", d: "Who a Ngawilyi Aunty is — narrative therapists blending therapy, mentoring and cultural connection, with the qualifications and safeguards behind the model." },
  "/approach": { t: "Our Approach | Ngawilyi Narrative", d: "How Ngawilyi works — story, connection, culture, movement, mentoring, community and knowledge, woven into one consistent relationship." },
  "/programs": { t: "Programs | Ngawilyi Narrative", d: "Proposed Ngawilyi programs including Those Who Know Not the Adults That Don't, Turning Story, Education Outside the Box, family connection, therapy, mentoring and movement." },
  "/threads": { t: "Finding the Threads | Ngawilyi Narrative", d: "Reconnection, identity and the stories that sit between the records — a narrative-informed approach to Aboriginal family stories, with a guide and organisational training." },
  "/who-we-support": { t: "Who We Support | Ngawilyi Narrative", d: "Children and young people, parents, kinship carers, foster carers, Aboriginal communities, schools and professionals — each met with warmth and plain English." },
  "/culture": { t: "Cultural Identity & Connection | Ngawilyi Narrative", d: "Ngawilyi means Aunty in Arabana language, authorised through Arabana Elders and the Arabana Aboriginal Corporation. Culture, family, community, identity and belonging." },
  "/community": { t: "Community Programs | Ngawilyi Narrative", d: "Art & Cuppa with Aunties, Community Cook-Up, Yarning Circles and Community Movement — community building is part of the intervention." },
  "/consultation": { t: "Help Shape Ngawilyi | Ngawilyi Narrative", d: "Ngawilyi Narrative is being built with community, not simply delivered to community. Join a community yarn, consultation or co-design." },
  "/referrals": { t: "Make a Referral | Ngawilyi Narrative", d: "Refer a child, young person or family to Ngawilyi Narrative — referrals based on what is happening in life, not a perfect program pick." },
  "/impact": { t: "Impact & Evaluation | Ngawilyi Narrative", d: "How Ngawilyi Narrative intends to evaluate impact honestly — participant-defined goals, stories of change and community voice." },
  "/partner": { t: "Partner With Us | Ngawilyi Narrative", d: "One relationship. Multiple life domains. Less system overwhelm. Nine ways to partner with Ngawilyi Narrative — pilots, ACCOs, education, government, philanthropy and research." },
  "/contact": { t: "Contact | Ngawilyi Narrative", d: "Every partnership, referral and program starts with a yarn. Contact Ngawilyi Narrative." },
  "/request-support": { t: "Request Support | Ngawilyi Narrative", d: "What brings you to Ngawilyi? Private support, NDIS supports, service-funded referrals or a yarn — find your pathway." },
  "/private-supports": { t: "Private Narrative Therapy & Support | Ngawilyi Narrative", d: "Private narrative therapy, mentoring and movement support — no referral or NDIS plan needed. Accessible fees for community." },
  "/ndis-supports": { t: "NDIS Supports | Ngawilyi Narrative", d: "Culturally responsive NDIS supports — mentoring, capacity building, skill development and community participation for people with an active NDIS plan." },
  "/pricing": { t: "Pricing & Fees | Ngawilyi Narrative", d: "Suggested pricing for Ngawilyi Narrative supports — free initial yarn, narrative therapy, mentoring, family work, consultation and training. Arabana community discount available." },
  "/privacy": { t: "Privacy, Consent & Safety | Ngawilyi Narrative", d: "Your story belongs to you. How Ngawilyi Narrative holds your information, your consent choices, and cancellation policy." },
  "/cultural-governance": { t: "Cultural Governance Committee | Ngawilyi Narrative", d: "Ngawilyi Narrative is establishing a Cultural Governance Committee to bring cultural guidance, community perspectives and shared reflection into the organisation's development.", noindex: true },
  "/admin": { t: "Admin | Ngawilyi Narrative", d: "Ngawilyi Narrative admin area.", noindex: true },
  "/admin/login": { t: "Admin Sign In | Ngawilyi Narrative", d: "Ngawilyi Narrative admin area.", noindex: true },
};

const PageSEO = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    const seo = SEO_MAP[pathname] || SEO_MAP["/"];
    document.title = seo.t;
    const setMeta = (sel, val) => {
      const el = document.querySelector(sel);
      if (el) el.setAttribute("content", val);
    };
    setMeta('meta[name="description"]', seo.d);
    setMeta('meta[property="og:title"]', seo.t);
    setMeta('meta[property="og:description"]', seo.d);
    setMeta('meta[name="twitter:title"]', seo.t);
    setMeta('meta[name="twitter:description"]', seo.d);
    setMeta('link[rel="canonical"]', "");
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute("href", `https://ngawilyinarrative.com.au${pathname}`);
    let robots = document.querySelector('meta[name="robots"]');
    if (seo.noindex) {
      if (!robots) {
        robots = document.createElement("meta");
        robots.setAttribute("name", "robots");
        document.head.appendChild(robots);
      }
      robots.setAttribute("content", "noindex");
    } else if (robots) {
      robots.remove();
    }
  }, [pathname]);
  return null;
};

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

function App() {
  useEffect(() => {
    const lenis = new Lenis({ lerp: 0.09, wheelMultiplier: 1 });
    const raf = (time) => {
      lenis.raf(time);
      requestAnimationFrame(raf);
    };
    const id = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(id);
      lenis.destroy();
    };
  }, []);

  return (
    <BrowserRouter>
      <ScrollToTop />
      <PageSEO />
      <Layout>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/about" element={<About />} />
          <Route path="/aunties" element={<Aunties />} />
          {SHOW_GOVERNANCE && (
            <Route path="/cultural-governance" element={<CulturalGovernance />} />
          )}
          <Route path="/approach" element={<Approach />} />
          <Route path="/programs" element={<Programs />} />
          <Route path="/threads" element={<Threads />} />
          <Route path="/who-we-support" element={<WhoWeSupport />} />
          <Route path="/culture" element={<Culture />} />
          <Route path="/community" element={<Community />} />
          <Route path="/consultation" element={<Consultation />} />
          <Route path="/referrals" element={<Referrals />} />
          <Route path="/impact" element={<Impact />} />
          <Route path="/partner" element={<Partner />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/request-support" element={<RequestSupport />} />
          <Route path="/private-supports" element={<PrivateSupports />} />
          <Route path="/ndis-supports" element={<NdisSupports />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<AdminDashboard />} />
        </Routes>
      </Layout>
      <Toaster position="bottom-right" toastOptions={{ style: { background: "#2D2A26", color: "#FDFBF7", border: "none", borderRadius: "18px" } }} />
    </BrowserRouter>
  );
}

export default App;
